import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, GraduationCap, ShieldCheck, Network, HeartHandshake } from "lucide-react";
import { Reveal, MaskedLine, SectionLabel } from "@/components/Reveal";

const values = [
  { icon: GraduationCap, title: "Expertise certifiée", text: "Titulaire du Titre Professionnel RPMS (Responsable de Petite et Moyenne Structure) de niveau 5." },
  { icon: Network, title: "Réseau AADPROX", text: "Membre du réseau national : outils, méthodes, formations régulières et veille juridique." },
  { icon: ShieldCheck, title: "Sérénité garantie", text: "Activité couverte par une assurance Responsabilité Civile Professionnelle." },
  { icon: HeartHandshake, title: "Proximité & confiance", text: "Une relation humaine et durable, sur site ou à distance, selon vos besoins." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="mx-auto max-w-7xl px-6 pb-24 pt-16 lg:px-10 lg:pt-24">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="relative order-2 lg:order-1 lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-2xl"
            >
              <img
                src="/assets/aurelie.png"
                alt="Aurélie Violet, fondatrice d'ARGIA Admin"
                data-testid="about-portrait"
                className="w-full object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="absolute -bottom-6 -right-4 rounded-xl bg-white px-5 py-3 shadow-lg border border-[#2C2A29]/10"
            >
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-argia-muted">Franchisée</p>
              <img src="/assets/logo-aadprox-wide.png" alt="Franchisée du réseau AADPROX" className="h-12 w-auto" data-testid="about-aadprox-logo" />
            </motion.div>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-7">
            <Reveal delay={0.1}>
              <SectionLabel>À propos</SectionLabel>
            </Reveal>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.08] tracking-tight text-argia-ink sm:text-5xl lg:text-6xl" data-testid="about-title">
              <MaskedLine delay={0.2}>Aurélie Violet,</MaskedLine>
              <MaskedLine delay={0.32}>
                la clarté en <em className="italic font-medium text-argia-red">héritage.</em>
              </MaskedLine>
            </h1>
            <Reveal delay={0.6}>
              <div className="mt-8 space-y-5 text-base leading-relaxed text-argia-muted md:text-lg">
                <p>
                  <em className="font-display italic text-argia-ink">Argia</em> signifie « lumière » en basque —
                  c'est exactement ce que je souhaite apporter à votre administratif : de la clarté.
                </p>
                <p>
                  Gestionnaire administrative indépendante, forte de plusieurs années d'expérience dans les domaines
                  de l'administration, de la relation client et de la gestion d'entreprise, j'accompagne les
                  professionnels et les particuliers dans leurs démarches et l'organisation de leur activité.
                </p>
                <p>
                  Mon parcours d'assistante SAV et administrative, puis de responsable de secteur, m'a permis de
                  développer une expertise en gestion administrative, ressources humaines, organisation et suivi de
                  clientèle — que je mets aujourd'hui au service de mes clients.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.8}>
              <Link
                to="/rendez-vous"
                data-testid="about-cta-rdv"
                className="group mt-10 inline-flex items-center gap-2 rounded-full bg-argia-red px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-argia-redDark hover:-translate-y-0.5 hover:shadow-lg"
              >
                Faisons connaissance
                <ArrowRight size={16} strokeWidth={1.5} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-argia-stone py-24" data-testid="about-values">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <SectionLabel>Mes engagements</SectionLabel>
            <h2 className="mt-5 max-w-2xl font-display text-3xl font-bold tracking-tight text-argia-ink sm:text-4xl">
              Des garanties concrètes pour une collaboration sereine
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.08}>
                <motion.div
                  whileHover={{ y: -4 }}
                  className="h-full rounded-2xl border border-[#2C2A29]/10 bg-white p-8 transition-shadow duration-300 hover:shadow-lg"
                >
                  <v.icon size={26} strokeWidth={1.5} className="text-argia-red" />
                  <h3 className="mt-5 font-display text-lg font-bold text-argia-ink">{v.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-argia-muted">{v.text}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
